import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { US_STATES_LIST } from "@/lib/states";
import { fplIncomeBands, fplBase } from "@/lib/fpl";
import { HeartHandshake } from "lucide-react";
import AssistanceApplication from "@/components/AssistanceApplication";
import { t } from "@/lib/i18n";

const ELEVATED = ["action", "high", "very-high"];

const AssistanceScreening = ({ latestReading }) => {
  const elevated = ELEVATED.includes(latestReading?.classification?.level);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    state: "",
    zip_code: "",
    tenure: "own",
    latest_pci_l: latestReading?.pci_l ?? "",
    household_size: 2,
    income_band: "at_or_below_100_fpl",
    pro_confirmed: false,
    prior_assistance: false,
  });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post("/assistance", {
        ...form,
        latest_pci_l: Number(form.latest_pci_l),
        household_size: Number(form.household_size),
      });
      setResult(data.evaluation);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (result) {
    return (
      <>
        <div className="card-flat" data-testid="assistance-result">
          <div className="flex items-center gap-2">
            <HeartHandshake size={16} className="text-[#2F524A]" />
            <h3 className="font-serif text-xl">{t("Radon Mitigation Assistance")}</h3>
          </div>
          <p
            className="mt-3 text-lg font-serif text-[#1C201F]"
            data-testid="assistance-qualifies"
            data-qualifies={result.qualifies}
          >
            {result.qualifies ? t("You may qualify.") : t("You may not qualify.")}
          </p>
          <p className="text-sm text-[#5C6661] mt-2">{t(result.message)}</p>
          {result.reasons?.length ? (
            <ul className="mt-3 space-y-1 text-xs text-[#5C6661] list-disc list-inside">
              {result.reasons.map((r) => <li key={r}>{t(r)}</li>)}
            </ul>
          ) : null}
          {result.next_step && (
            <p className="text-sm text-[#2F524A] font-semibold mt-3">{t(result.next_step)}</p>
          )}
          <button onClick={() => setResult(null)} className="text-xs text-[#2F524A] hover:underline mt-4">{t("Retake screening")}</button>
        </div>
        {result.qualifies && (
          <div className="mt-6">
            <AssistanceApplication />
          </div>
        )}
      </>
    );
  }

  if (!open) {
    return (
      <div
        className={`card-flat ${elevated ? "!border-[#2F524A] !border-2 bg-[#F4F7F6]" : ""}`}
        data-testid="assistance-callout"
        data-elevated={elevated}
      >
        {elevated && (
          <p className="eyebrow text-[#2F524A] mb-2" data-testid="assistance-elevated-badge">
            {t("Recommended for your latest reading")}
          </p>
        )}
        <div className="flex items-center gap-2">
          <HeartHandshake size={16} className="text-[#2F524A]" />
          <h3 className="font-serif text-xl">{t("Need help paying for mitigation?")}</h3>
        </div>
        <p className="text-sm text-[#5C6661] mt-2 leading-relaxed">
          {t("Answer a few quick questions to see if you may qualify for a Radon Community Mitigation Installation Project.")}
        </p>
        <button
          onClick={() => setOpen(true)}
          className="btn-primary mt-4 !py-2 !px-4 text-sm"
          data-testid="assistance-open-button"
        >
          {t("Start the screening")}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-flat space-y-4" data-testid="assistance-form">
      <div className="flex items-center gap-2">
        <HeartHandshake size={16} className="text-[#2F524A]" />
        <h3 className="font-serif text-xl">{t("Radon Mitigation Assistance screening")}</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="eyebrow block mb-2">{t("State *")}</label>
          <select required className="input-field" value={form.state}
            onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
            data-testid="assistance-state">
            <option value="" disabled>—</option>
            {US_STATES_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("ZIP *")}</label>
          <input required pattern="\d{5}" maxLength={5} minLength={5} className="input-field"
            value={form.zip_code} onChange={(e) => setForm((f) => ({ ...f, zip_code: e.target.value }))}
            data-testid="assistance-zip" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Home tenure")}</label>
          <select className="input-field" value={form.tenure}
            onChange={(e) => setForm((f) => ({ ...f, tenure: e.target.value }))}
            data-testid="assistance-tenure">
            <option value="own">{t("Own")}</option>
            <option value="rent">{t("Rent")}</option>
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Latest reading (pCi/L) *")}</label>
          <input required type="number" step="0.1" min={0} className="input-field"
            value={form.latest_pci_l}
            onChange={(e) => setForm((f) => ({ ...f, latest_pci_l: e.target.value }))}
            data-testid="assistance-pci-l" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Household size")}</label>
          <input type="number" min={1} max={30} className="input-field"
            value={form.household_size}
            onChange={(e) => setForm((f) => ({ ...f, household_size: e.target.value }))}
            data-testid="assistance-household" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Approximate income")}</label>
          <select className="input-field" value={form.income_band}
            onChange={(e) => setForm((f) => ({ ...f, income_band: e.target.value }))}
            data-testid="assistance-income">
            {fplIncomeBands(form.household_size || 1).map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
          <p className="text-xs text-[#5C6661] mt-1.5">
            {t("Ranges use the 2026 Federal Poverty Level for a household of")}{" "}
            {form.household_size || 1} (base {`$${fplBase(form.household_size || 1).toLocaleString()}`}).
          </p>
        </div>
        <label className="flex items-center gap-2 sm:col-span-2 p-3 rounded-xl border border-[#E8ECEA] cursor-pointer hover:border-[#2F524A]">
          <input type="checkbox" className="accent-[#2F524A]" checked={form.pro_confirmed}
            onChange={(e) => setForm((f) => ({ ...f, pro_confirmed: e.target.checked }))}
            data-testid="assistance-pro" />
          <span className="text-sm">{t("A professional confirmed my radon result.")}</span>
        </label>
        <label className="flex items-center gap-2 sm:col-span-2 p-3 rounded-xl border border-[#E8ECEA] cursor-pointer hover:border-[#2F524A]">
          <input type="checkbox" className="accent-[#2F524A]" checked={form.prior_assistance}
            onChange={(e) => setForm((f) => ({ ...f, prior_assistance: e.target.checked }))}
            data-testid="assistance-prior" />
          <span className="text-sm">{t("I have received radon mitigation assistance before.")}</span>
        </label>
      </div>

      {error && <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2">{error}</div>}

      <div className="flex justify-end gap-3">
        <button type="button" onClick={() => setOpen(false)} className="btn-secondary !py-2 !px-4 text-sm">{t("Cancel")}</button>
        <button type="submit" disabled={saving} className="btn-primary !py-2 !px-5 text-sm"
          data-testid="assistance-submit">
          {saving ? t("Checking…") : t("Check eligibility")}
        </button>
      </div>
    </form>
  );
};

export default AssistanceScreening;
